import React, { createContext, useState, useContext, useEffect } from 'react';
import { ServerConfigContext } from './ServerConfigContext';

// AuthContext 생성
export const JWTAuthContext = createContext();

// API 기본 URL (프록시 패턴 사용)
const getApiBaseUrl = () => {
  // 개발 환경에서는 프록시 사용: /api/{server_id}/
  if (process.env.NODE_ENV === 'development') {
    return '/board-api/api/v1';
  }

  // 프로덕션 환경에서는 환경변수 또는 기본값 사용
  return process.env.REACT_APP_BOARD_API_URL || 'http://localhost:8080/api/v1';
};

// AuthProvider 컴포넌트
export const JWTAuthProvider = ({ children }) => {
  const { getSmartApiUrl } = useContext(ServerConfigContext) || {};
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // 컴포넌트 마운트 시 localStorage에서 토큰과 사용자 정보 로드
  useEffect(() => {
    const initializeAuth = () => {
      try {
        const storedToken = localStorage.getItem('jwtToken');
        const storedUser = localStorage.getItem('user');
        
        if (process.env.NODE_ENV === 'development') {
          console.log('🔍 [JWTAuthContext] localStorage 확인:', {
            hasToken: !!storedToken,
            tokenLength: storedToken?.length,
            hasUser: !!storedUser
          });
        }

        if (storedToken && storedUser) {
          const userData = JSON.parse(storedUser);
          setToken(storedToken);
          setUser(userData);
          setIsAuthenticated(true);
          console.log('✅ [JWTAuth] 토큰 복원 완료:', {
            userId: userData.user_id,
            tokenLength: storedToken.length,
            tokenStart: storedToken.substring(0, 20) + '...'
          });
        } else {
          console.log('ℹ️ [JWTAuth] 저장된 인증 정보 없음');
        }
      } catch (error) {
        console.error('❌ 인증 정보 복원 실패:', error);
        // 손상된 데이터 정리
        localStorage.removeItem('jwtToken');
        localStorage.removeItem('user');
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // localStorage 변경 감지 (다른 탭이나 같은 탭에서 로그인 시)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'jwtToken' || e.key === 'user') {
        if (process.env.NODE_ENV === 'development') {
          console.log('📡 [JWTAuthContext] localStorage 변경 감지:', e.key);
        }

        const storedToken = localStorage.getItem('jwtToken');
        const storedUser = localStorage.getItem('user');

        if (storedToken && storedUser) {
          try {
            const userData = JSON.parse(storedUser);
            setToken(storedToken);
            setUser(userData);
            setIsAuthenticated(true);
            if (process.env.NODE_ENV === 'development') {
              console.log('✅ [JWTAuthContext] 로그인 상태 동기화 완료:', userData.nickname);
            }
          } catch (error) {
            console.error('❌ [JWTAuthContext] 사용자 데이터 파싱 오류:', error);
          }
        } else {
          // 로그아웃된 경우
          setToken(null);
          setUser(null);
          setIsAuthenticated(false);
          if (process.env.NODE_ENV === 'development') {
            console.log('📤 [JWTAuthContext] 로그아웃 상태 동기화 완료');
          }
        }
      }
    };

    // 같은 탭에서도 변경을 감지하기 위한 커스텀 이벤트 리스너
    const handleCustomStorageChange = (e) => {
      handleStorageChange(e.detail);
    };

    // 브라우저 storage 이벤트 (다른 탭)
    window.addEventListener('storage', handleStorageChange);
    // 커스텀 이벤트 (같은 탭)
    window.addEventListener('localStorageChange', handleCustomStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('localStorageChange', handleCustomStorageChange);
    };
  }, []);

  // 공통 API 호출 함수
  const apiCall = async (endpoint, options = {}) => {
    try {
      let url;
      if (endpoint.startsWith('http')) {
        url = endpoint;
      } else if (getSmartApiUrl && endpoint.startsWith('/auth/')) {
        // /auth/* 엔드포인트는 스마트 API URL 사용 (auth-api-server로 자동 라우팅)
        url = getSmartApiUrl(endpoint);
      } else if (endpoint.startsWith('/stock-db-api') || endpoint.startsWith('/stock-api') ||
                 endpoint.startsWith('/alert-api') || endpoint.startsWith('/chart-api') ||
                 endpoint.startsWith('/stock-report-api')) {
        // 다른 프록시 경로는 그대로 사용 (setupProxy.js에서 처리)
        url = endpoint;
      } else {
        // 기존 방식 사용 (board-api)
        url = `${getApiBaseUrl()}${endpoint}`;
      }
      
      const defaultHeaders = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      };

      // JWT 토큰이 있으면 Authorization 헤더 추가
      if (token) {
        defaultHeaders['Authorization'] = `Bearer ${token}`;
        console.log(`🔑 [JWTAuth] Authorization 헤더 추가 완료: Bearer ${token.substring(0, 20)}...`);
      } else {
        console.warn(`⚠️ [JWTAuth] 토큰 없음! Authorization 헤더 추가 안됨`);
      }

      const config = {
        ...options,
        headers: {
          ...defaultHeaders,
          ...options.headers,
        },
      };

      console.log(`📞 API 호출: ${options.method || 'GET'} ${url}`, 'Headers:', config.headers);
      
      const response = await fetch(url, config);
      
      // 401 에러 시 자동 로그아웃 처리
      if (response.status === 401) {
        console.warn('⚠️ 401 Unauthorized - 자동 로그아웃 처리');
        logout();
        throw new Error('인증이 만료되었습니다. 다시 로그인해주세요.');
      }

      // 응답이 JSON인지 확인
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const data = await response.json();
        
        if (!response.ok) {
          throw new Error(data.message || `HTTP ${response.status}: ${response.statusText}`);
        }
        
        return data;
      } else {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        return null;
      }
    } catch (error) {
      console.error('❌ API 호출 실패:', error);
      throw error;
    }
  };

  // 로그인 함수
  const login = async (userId, password) => {
    try {
      setIsLoading(true);
      
      const response = await apiCall('/auth/login', {
        method: 'POST',
        body: JSON.stringify({
          user_id: userId,
          password: password,
        }),
      });

      if (response.success && response.data) {
        const { token: newToken, user: userData } = response.data;
        
        // 상태 업데이트
        setToken(newToken);
        setUser(userData);
        setIsAuthenticated(true);
        
        // localStorage에 저장
        localStorage.setItem('jwtToken', newToken);
        localStorage.setItem('user', JSON.stringify(userData));
        
        console.log('✅ 로그인 성공:', {
          userId: userData.user_id,
          tokenLength: newToken.length,
          tokenStart: newToken.substring(0, 20) + '...',
          isStoredInLocalStorage: localStorage.getItem('jwtToken') === newToken
        });
        return { success: true, message: '로그인 성공', user: userData };
      } else {
        throw new Error(response.message || '로그인 실패');
      }
    } catch (error) {
      console.error('❌ 로그인 실패:', error);
      return { success: false, message: error.message };
    } finally {
      setIsLoading(false);
    }
  };

  // 로그아웃 함수
  const logout = () => {
    console.log('🚪 로그아웃 처리');
    
    // 상태 초기화
    setUser(null);
    setToken(null);
    setIsAuthenticated(false);
    
    // localStorage에서 제거
    localStorage.removeItem('jwtToken');
    localStorage.removeItem('user');
    
    return { success: true, message: '로그아웃 완료' };
  };

  // 사용자 프로필 새로고침
  const refreshUser = async () => {
    try {
      if (!token) {
        throw new Error('토큰이 없습니다.');
      }

      const response = await apiCall('/auth/profile');
      
      if (response.success && response.data) {
        setUser(response.data);
        localStorage.setItem('user', JSON.stringify(response.data));
        console.log('✅ 사용자 프로필 새로고침 완료');
        return response.data;
      } else {
        throw new Error(response.message || '프로필 조회 실패');
      }
    } catch (error) {
      console.error('❌ 프로필 새로고침 실패:', error);
      // 프로필 조회 실패 시 로그아웃 처리
      logout();
      throw error;
    }
  };

  // Context 값
  const contextValue = {
    // 상태
    user,
    token,
    isLoading,
    isAuthenticated,
    
    // 함수
    login,
    logout,
    apiCall,
    refreshUser,
  };

  return (
    <JWTAuthContext.Provider value={contextValue}>
      {children}
    </JWTAuthContext.Provider>
  );
};

// AuthContext를 사용하는 커스텀 훅
export const useJWTAuth = () => {
  const context = useContext(JWTAuthContext);
  if (!context) {
    throw new Error('useJWTAuth는 JWTAuthProvider 내에서 사용되어야 합니다.');
  }
  return context;
};